import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";
import Loader from "react-loader-spinner";
import moment from "moment";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { DecryptED } from "../../DecryptJS/Decrypt";
import { cryptoED } from "./cryptoED";
import { SendNotification } from "./SendNotification";

/* ─────────────────────────────────────────────
   MODULE-LEVEL HELPERS  (accessible by both components)
───────────────────────────────────────────── */
function priorityColor(p) {
  return p === "High"
    ? { bg: "#fef2f2", color: "#dc2626", border: "#fca5a5" }
    : p === "Medium"
      ? { bg: "#fffbeb", color: "#d97706", border: "#fcd34d" }
      : { bg: "#f0fdf4", color: "#16a34a", border: "#86efac" };
}

function statusColor(s) {
  return s === "Open"
    ? { bg: "#fef2f2", color: "#dc2626", border: "#fca5a5" }
    : { bg: "#f0fdf4", color: "#16a34a", border: "#86efac" };
}

/* ─────────────────────────────────────────────
   REPLY POPUP COMPONENT
───────────────────────────────────────────── */
function ReplyPopup({ ticket, onClose, onSubmit, userName, sid, uid, userType }) {
  const [replyText, setReplyText] = useState("");
  const [replyType, setReplyType] = useState("reply");
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [activityList, setActivityList] = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const textRef = useRef(null);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevWidth = document.body.style.width;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";

    // Fetch history on popup open
    fetchHistory();

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.width = prevWidth;
    };
  }, []);

  function fetchHistory() {
    setHistLoading(true);
    axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
      fun_name: "GetTicketHistory", sid, ticket_id: ticket.id,
    })
      .then(function (res) {
        var d = res.data;
        var hist = (d && d.Ticket_History) || (d && d.TicketHistory) || (d && d.History) || (d && d.history) ||
          (d && d.ticket_history) || (Array.isArray(d) ? d : null) || [];
        if (Array.isArray(hist) && hist.length > 0) {
          hist = hist.slice().sort(function (a, b) {
            return moment(b.date, "YYYY-MM-DD HH:mm:ss").valueOf() - moment(a.date, "YYYY-MM-DD HH:mm:ss").valueOf();
          });
          setActivityList(hist);
        } else {
          var fb = [];
          if (ticket.updated_on) fb.push({ staff: ticket.staff || "System", date: ticket.updated_on, history: "Ticket last updated", history_details: ticket.remark || "", reply: "0" });
          if (ticket.created_on || ticket.date) fb.push({ staff: ticket.name || "Creator", date: ticket.created_on || ticket.date, history: "Ticket created", history_details: ticket.title || "", reply: "0" });
          setActivityList(fb);
        }
        setHistLoading(false);
      })
      .catch(function () {
        var fb = [];
        if (ticket.updated_on) fb.push({ staff: ticket.staff || "System", date: ticket.updated_on, history: "Last updated", history_details: ticket.remark || "", reply: "0" });
        if (ticket.date) fb.push({ staff: ticket.name || "Creator", date: ticket.date, history: "Ticket created", history_details: "", reply: "0" });
        setActivityList(fb);
        setHistLoading(false);
      });
  }

  function handleSubmit() {
    if (!replyText.trim() || loading) return;
    setLoading(true);
    var nam = userName ? (userName.charAt(0).toUpperCase() + userName.slice(1).toLowerCase()) : "User";
    var newStatus = ticket.status === "Closed" ? "Open" : ticket.status;
    var replyPromise = axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
      fun_name: "TicketHistoryInsert", sid, ticket_id: ticket.id,
      date: moment().format("YYYY-MM-DD HH:mm:ss"), committee: ticket.committee,
      remark_reply: replyText, reply: replyType === "reply" ? 1 : 0,
      staff: nam, status: newStatus,
      history: nam + " replied on ticket #" + ticket.id,
      history_details: replyText, uid, is_sadmin: userType === "superadmin" ? 1 : 0,
    });
    // Update ticket (reopen if closed, and update timestamp)
    var updatePromise = axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
      fun_name: "updateTicket", sid, ticket_id: ticket.id,
      status: ticket.status === "Closed" ? "Open" : ticket.status,
      updated_on: moment().format("YYYY-MM-DD HH:mm:ss"),
    });
    Promise.all([replyPromise, updatePromise])
      .then(function () {
        setSubmitSuccess(true); setReplyText("");
        if (ticket.status === "Closed") ticket.status = "Open";
        ticket.updated_on = moment().format("YYYY-MM-DD HH:mm:ss");
        // Send SMS notification to ticket creator
        if (ticket.uid || ticket.staff_id) {
          SendNotification({
            sid: sid,
            userid: ticket.uid || ticket.staff_id,
            ticket_id: ticket.id,
            action: nam + " replied on your ticket #" + ticket.id,
          });
        }
        if (onSubmit) onSubmit();
        fetchHistory();
        setTimeout(function () { setSubmitSuccess(false); }, 2500);
      })
      .catch(function (e) { console.error("Reply submit error:", e); })
      .finally(function () { setLoading(false); });
  }

  function handleSubmitAndClose() {
    if (!replyText.trim() || loading) return;
    setLoading(true);
    var nam = userName ? (userName.charAt(0).toUpperCase() + userName.slice(1).toLowerCase()) : "User";
    var replyPromise = axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
      fun_name: "TicketHistoryInsert", sid, ticket_id: ticket.id,
      date: moment().format("YYYY-MM-DD HH:mm:ss"), committee: ticket.committee,
      remark_reply: replyText, reply: replyType === "reply" ? 1 : 0,
      staff: nam, status: "Closed",
      history: nam + " replied and closed ticket #" + ticket.id,
      history_details: replyText, uid, is_sadmin: userType === "superadmin" ? 1 : 0,
    });
    var closePromise = axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
      fun_name: "updateTicket", sid, ticket_id: ticket.id,
      status: "Closed",
      updated_on: moment().format("YYYY-MM-DD HH:mm:ss"),
    });
    Promise.all([replyPromise, closePromise])
      .then(function () {
        setSubmitSuccess(true); setReplyText("");
        ticket.status = "Closed";
        ticket.updated_on = moment().format("YYYY-MM-DD HH:mm:ss");
        if (ticket.uid || ticket.staff_id) {
          SendNotification({
            sid: sid,
            userid: ticket.uid || ticket.staff_id,
            ticket_id: ticket.id,
            action: nam + " replied and closed your ticket #" + ticket.id,
          });
        }
        if (onSubmit) onSubmit();
        fetchHistory();
        setTimeout(function () { setSubmitSuccess(false); onClose(); }, 1500);
      })
      .catch(function (e) { console.error("Reply & Close error:", e); })
      .finally(function () { setLoading(false); });
  }

  var pColor = priorityColor(ticket.priority);

  // ── Inline style objects ──
  var S = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      zIndex: 999999,   // 🔥 VERY HIGH
      background: "rgba(11,37,69,0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }, modal: { background: "#fff", borderRadius: window.innerWidth <= 480 ? "12px" : "18px", width: window.innerWidth <= 480 ? "95%" : "100%", maxWidth: "680px", maxHeight: window.innerWidth <= 480 ? "95vh" : "90vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 80px rgba(11,37,69,0.32)", overflow: "hidden", margin: window.innerWidth <= 480 ? "0 auto" : undefined },
    header: { padding: window.innerWidth <= 480 ? "14px 14px 12px" : "20px 24px 16px", background: "linear-gradient(135deg,#0b2545 0%,#1b5499 100%)", position: "relative", flexShrink: 0 },
    titleRow: { fontSize: "15px", fontWeight: 800, color: "#fff", display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" },
    ticketId: { background: "rgba(255,255,255,0.18)", color: "#fff", padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 700, flexShrink: 0 },
    metaRow: { display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" },
    chip: { background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.9)", padding: "4px 12px", borderRadius: "20px", fontSize: "11.5px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "5px" },
    labelsRow: { display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" },
    label: { display: "inline-flex", alignItems: "center", gap: "6px", padding: "5px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: 700, border: "1.5px solid" },
    closeBtn: { position: "absolute", top: "16px", right: "16px", width: "32px", height: "32px", borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: "18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
    body: { flex: 1, overflowY: "auto", padding: window.innerWidth <= 480 ? "14px 12px" : "20px 24px", display: "flex", flexDirection: "column", gap: window.innerWidth <= 480 ? "12px" : "18px" },
    sectionLabel: { fontSize: "11px", fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" },
    sectionLine: { flex: 1, height: "1px", background: "#e2eaf4" },
    descBox: { background: "#f4f7fb", borderRadius: "10px", padding: "12px 14px", border: "1px solid #e2eaf4", fontSize: "13px", color: "#4a6080", lineHeight: "1.6" },
    tlItem: { display: "flex", gap: "12px", paddingBottom: "14px", position: "relative" },
    tlDot: { width: "30px", height: "30px", borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", border: "2px solid #fff", zIndex: 1 },
    tlContent: { flex: 1, background: "#f4f7fb", borderRadius: "10px", padding: "10px 13px", border: "1px solid #e2eaf4" },
    tlTop: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px", flexWrap: "wrap", gap: "4px" },
    tlActor: { fontSize: "12.5px", fontWeight: 700, color: "#0b2545" },
    tlTime: { fontSize: "11px", color: "#94a3b8", fontWeight: 500 },
    tlAction: { fontSize: "11.5px", color: "#4a6080", fontWeight: 500 },
    tlDetail: { fontSize: "12px", color: "#64748b", marginTop: "5px", fontStyle: "italic" },
    tabRow: { display: "flex", gap: "6px", marginBottom: "12px" },
    tabBase: { flex: 1, padding: "8px", borderRadius: "8px", border: "1.5px solid #e2eaf4", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", background: "#f4f7fb", color: "#4a6080", fontFamily: "inherit" },
    tabActive: { flex: 1, padding: "8px", borderRadius: "8px", border: "1.5px solid transparent", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", background: "linear-gradient(135deg,#0b2545,#1b5499)", color: "#fff", fontFamily: "inherit" },
    textarea: { width: "100%", minHeight: "110px", padding: "12px 14px", border: "1.5px solid #e2eaf4", borderRadius: "10px", fontSize: "13px", fontWeight: 500, fontFamily: "inherit", color: "#0b2545", background: "#f4f7fb", outline: "none", resize: "vertical", boxSizing: "border-box" },
    footer: { padding: window.innerWidth <= 480 ? "10px 12px" : "14px 24px", borderTop: "1px solid #e2eaf4", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", background: "#fafcff", flexShrink: 0, flexWrap: window.innerWidth <= 480 ? "wrap" : "nowrap" },
    charCount: { fontSize: "11.5px", color: "#94a3b8", fontWeight: 500 },
    btnCancel: { padding: "9px 20px", borderRadius: "9px", border: "1.5px solid #e2eaf4", background: "#fff", color: "#4a6080", fontSize: "13px", fontWeight: 700, fontFamily: "inherit", cursor: "pointer" },
    btnSubmitOn: { padding: "9px 22px", borderRadius: "9px", border: "none", background: "linear-gradient(135deg,#0b2545,#1b5499)", color: "#fff", fontSize: "13px", fontWeight: 800, fontFamily: "inherit", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", boxShadow: "0 3px 10px rgba(11,37,69,0.22)", opacity: 1 },
    btnSubmitOff: { padding: "9px 22px", borderRadius: "9px", border: "none", background: "linear-gradient(135deg,#0b2545,#1b5499)", color: "#fff", fontSize: "13px", fontWeight: 800, fontFamily: "inherit", cursor: "not-allowed", display: "flex", alignItems: "center", gap: "6px", opacity: 0.45 },
  };

  return (
    <div style={S.overlay} onClick={(e) => e.stopPropagation()}>
      <div style={S.modal}>

        {/* ── Header ── */}
        <div style={S.header}>
          <div style={S.titleRow}>
            <span style={S.ticketId}>#{ticket.id}</span>
            <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ticket.title}</span>
          </div>
          <div style={S.metaRow}>
            <span style={S.chip}>🏷 {ticket.committee}</span>
            <span style={S.chip}>📅 {moment(ticket.date, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY")}</span>
            <span style={{ ...S.chip, background: ticket.status === "Open" ? "rgba(220,38,38,0.18)" : "rgba(22,163,74,0.18)", borderColor: ticket.status === "Open" ? "rgba(220,38,38,0.3)" : "rgba(22,163,74,0.3)", color: ticket.status === "Open" ? "#fca5a5" : "#86efac" }}>
              {ticket.status === "Open" ? "⏳" : "✅"} {ticket.status}
            </span>
            <span style={S.chip}>{ticket.priority === "High" ? "🔴" : ticket.priority === "Medium" ? "🟠" : "🟢"} {ticket.priority}</span>
          </div>
          <div style={S.labelsRow}>
            {ticket.name && <span style={{ ...S.label, background: "rgba(254,249,195,0.15)", color: "#fde047", borderColor: "rgba(253,224,71,0.3)" }}>✏️ Created by: <strong>{ticket.name}</strong></span>}
            {ticket.staff && <span style={{ ...S.label, background: "rgba(224,242,254,0.15)", color: "#7dd3fc", borderColor: "rgba(125,211,252,0.3)" }}>📥 Assigned: <strong>{ticket.staff}</strong></span>}
          </div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* ── Body ── */}
        <div style={S.body}>

          {ticket.description && (
            <div>
              <div style={S.sectionLabel}>Description <span style={S.sectionLine} /></div>
              <div style={S.descBox}>{ticket.description}</div>
            </div>
          )}

          <div>
            <div style={S.sectionLabel}>Chat <span style={S.sectionLine} /></div>
            {histLoading ? (
              <div style={{ textAlign: "center", padding: "24px", color: "#94a3b8", fontSize: "13px" }}>Loading messages...</div>
            ) : (function () {
              var replies = activityList.filter(function (h) {
                return (h.reply === 1 || h.reply === "1") && h.remark_reply;
              });
              if (replies.length === 0) {
                return <div style={{ textAlign: "center", padding: "20px", color: "#94a3b8", fontSize: "13px" }}>No messages yet</div>;
              }
              var sortedReplies = replies.slice().sort(function (a, b) {
                return moment(a.date, "YYYY-MM-DD HH:mm:ss").valueOf() - moment(b.date, "YYYY-MM-DD HH:mm:ss").valueOf();
              });
              // Group messages by date
              var grouped = {};
              sortedReplies.forEach(function (h) {
                var dateKey = moment(h.date, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY");
                if (!grouped[dateKey]) grouped[dateKey] = [];
                grouped[dateKey].push(h);
              });
              return (
                <div style={{
                  maxHeight: "300px", overflowY: "auto",
                  background: "#f4f7fb",
                  borderRadius: "10px",
                  padding: "12px 10px",
                  border: "1px solid #e2eaf4",
                }}>
                  {Object.keys(grouped).map(function (dateKey, gi) {
                    return (
                      <div key={gi}>
                        {/* Date separator */}
                        <div style={{ textAlign: "center", margin: "8px 0 12px" }}>
                          <span style={{
                            background: "#e2eaf4", color: "#4a6080", fontSize: "11px", fontWeight: 700,
                            padding: "4px 14px", borderRadius: "8px",
                          }}>
                            {dateKey}
                          </span>
                        </div>
                        {grouped[dateKey].map(function (h, idx) {
                          var isMe = h.uid === cryptoED(uid);
                          return (
                            <div key={idx} style={{
                              display: "flex",
                              justifyContent: isMe ? "flex-end" : "flex-start",
                              marginBottom: "8px",
                            }}>
                              <div style={{ maxWidth: "75%" }}>
                                {/* Sender name for others */}
                                {!isMe && (
                                  <div style={{
                                    fontSize: "11px", fontWeight: 700, color: "#1b5499",
                                    marginBottom: "2px",
                                  }}>
                                    {h.staff}
                                  </div>
                                )}
                                {/* Chat bubble */}
                                <div style={{
                                  background: isMe ? "#dbeafe" : "#fff",
                                  borderRadius: isMe ? "10px 0px 10px 10px" : "0px 10px 10px 10px",
                                  padding: "8px 12px",
                                  border: isMe ? "1px solid #93c5fd" : "1px solid #e2eaf4",
                                  minWidth: "80px",
                                }}>
                                  <div style={{
                                    fontSize: "13px", color: "#0b2545",
                                    wordBreak: "break-word", lineHeight: "1.4",
                                  }}>
                                    {h.remark_reply}
                                  </div>
                                  <div style={{
                                    fontSize: "10px", color: "#94a3b8",
                                    textAlign: "right", marginTop: "4px",
                                  }}>
                                    {moment(h.date, "YYYY-MM-DD HH:mm:ss").format("hh:mm A")}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          <div>
            <div style={S.sectionLabel}>Add Reply <span style={S.sectionLine} /></div>
            {submitSuccess && (
              <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: "10px", padding: "10px 14px", marginBottom: "10px", color: "#16a34a", fontSize: "13px", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px" }}>
                ✅ Reply sent successfully!
              </div>
            )}
            <div style={S.tabRow}>
              <button style={replyType === "reply" ? S.tabActive : S.tabBase} onClick={() => setReplyType("reply")}>💬 Reply</button>
              <button style={replyType === "note" ? S.tabActive : S.tabBase} onClick={() => setReplyType("note")}>📝 Internal Note</button>
            </div>
            <textarea
              ref={textRef}
              style={S.textarea}
              placeholder={replyType === "reply" ? "Write your reply to this ticket..." : "Add an internal note (not visible to ticket creator)..."}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => { if (e.ctrlKey && e.key === "Enter") handleSubmit(); }}
            />
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={S.footer}>
          <div style={S.charCount}>{replyText.length > 0 ? replyText.length + " chars · Ctrl+Enter to submit" : "Ctrl+Enter to submit"}</div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button style={S.btnCancel} onClick={onClose}>✕ Close</button>
            {replyType === "reply" && (
              <button
                style={replyText.trim() && !loading ? { ...S.btnSubmitOn, background: "#dc2626" } : { ...S.btnSubmitOff }}
                onClick={handleSubmitAndClose}
                disabled={!replyText.trim() || loading}
              >
                {loading ? "⏳ Submitting..." : "📩 Reply & Close"}
              </button>
            )}
            <button style={replyText.trim() && !loading ? S.btnSubmitOn : S.btnSubmitOff} onClick={handleSubmit} disabled={!replyText.trim() || loading}>
              {loading ? "⏳ Submitting..." : replyType === "reply" ? "💬 Send Reply" : "📝 Save Note"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   TICKET CARD COMPONENT  (defined outside Tickets to avoid re-mount on every render)
───────────────────────────────────────────── */
function highlightMatch(text, query) {
  if (!query || !text) return text;
  var q = query.trim();
  if (!q) return text;
  try {
    var regex = new RegExp("(" + q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + ")", "gi");
    var parts = String(text).split(regex);
    if (parts.length <= 1) return text;
    return parts.map(function (part, i) {
      return regex.test(part)
        ? React.createElement("mark", { key: i, style: { background: "#fde047", color: "#1e293b", padding: "0 2px", borderRadius: "3px", fontWeight: 700 } }, part)
        : part;
    });
  } catch (e) { return text; }
}

function TicketCard({ ticket, k, onReply, onUpdateRemark, onNavigate, sid, isHighlighted, searchQuery }) {
  const pc = priorityColor(ticket.priority);
  const sc = statusColor(ticket.status);

  var updated = ticket.updated_on || ticket.date;
  var lastAct = updated ? moment(updated, "YYYY-MM-DD HH:mm:ss").fromNow() : null;

  return (
    <div id={`ticket-card-${ticket.id}`} className="tk-card" style={{
      animationDelay: `${k * 0.03}s`,
      boxShadow: isHighlighted ? "0 0 0 3px #3b82f6, 0 4px 16px rgba(59,130,246,0.3)" : undefined,
      transition: "box-shadow 0.5s ease",
    }}>
      {/* Labels row */}
      <div style={{ padding: "8px 20px 0", display: "flex", gap: "6px", flexWrap: "wrap" }}>
        {ticket.name && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            padding: "3px 10px", borderRadius: "20px", fontSize: "11.5px", fontWeight: 700,
            background: "#fef9c3", color: "#854d0e", border: "1.5px solid #fde047"
          }}>
            ✏️ Creator: <strong>{highlightMatch(ticket.name, searchQuery)}</strong>
          </span>
        )}
        {ticket.staff && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            padding: "3px 10px", borderRadius: "20px", fontSize: "11.5px", fontWeight: 700,
            background: "#e0f2fe", color: "#0369a1", border: "1.5px solid #7dd3fc"
          }}>
            📥 Assigned: <strong>{highlightMatch(ticket.staff, searchQuery)}</strong>
          </span>
        )}
        {updated && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            padding: "3px 10px", borderRadius: "20px", fontSize: "11.5px", fontWeight: 600,
            background: "#f4f7fb", color: "#64748b", border: "1.5px solid #e2eaf4",
            marginLeft: "auto"
          }}>
            🕐 Last Activity: {moment(updated, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY hh:mm A")} ({lastAct})
          </span>
        )}
      </div>

      <div className="tk-card-top" onClick={() => onNavigate(ticket.id, ticket.s_id)}>
        <div className="tk-card-left">
          <div className="tk-card-title-row">
            <span className="tk-ticket-id">#{ticket.id}</span>
            <span className="tk-card-title" title={ticket.description || ticket.title} style={{cursor: "pointer"}}>{highlightMatch(ticket.title, searchQuery)}</span>
          </div>
          <div className="tk-card-meta">
            <span className="tk-meta-item"><span className="tk-meta-icon">🏫</span> {highlightMatch(ticket.subschool || ticket.school, searchQuery)}</span>
            <span className="tk-meta-item"><span className="tk-meta-icon">🏷</span> {highlightMatch(ticket.committee, searchQuery)}</span>
            <span className="tk-meta-item"><span className="tk-meta-icon">📅</span>{moment(ticket.date, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY")}</span>
          </div>
        </div>
        <div className="tk-card-badges">
          <span className="tk-badge" style={{ background: pc.bg, color: pc.color, borderColor: pc.border }}>
            {ticket.priority === "High" ? "🔴" : ticket.priority === "Medium" ? "🟠" : "🟢"} {ticket.priority}
          </span>
          <span className="tk-badge" style={{ background: sc.bg, color: sc.color, borderColor: sc.border }}>
            {ticket.status === "Open" ? "⏳" : "✅"} {ticket.status}
          </span>
        </div>
      </div>

      <div className="tk-card-bottom">
        <div className="tk-duedate">
          <span>📆 Due:</span>
          <span className="tk-duedate-val">
            {ticket.duedate1
              ? ticket.duedate1.split(" ")[0] + " " + (ticket.duedate1.split(" ")[1] ? ticket.duedate1.split(" ")[1].slice(0, 3) : "")
              : ticket.duedate
                ? moment(ticket.duedate, "YYYY-MM-DD HH:mm:ss").format("DD MMM")
                : "N/A"}
          </span>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            onClick={(e) => { e.stopPropagation(); onReply(ticket); }}
            style={{
              height: "34px", padding: "0 14px",
              background: "linear-gradient(135deg, #0b2545, #1b5499)",
              color: "#fff", border: "none", borderRadius: "8px",
              fontSize: "12px", fontWeight: 700,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              cursor: "pointer", display: "flex", alignItems: "center", gap: "5px",
              boxShadow: "0 2px 8px rgba(11,37,69,0.2)",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-1px)"}
            onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
          >
            💬 Reply
          </button>
          <select
            className="tk-remark-select"
            onChange={(e) => {
              if (e.target.value !== "" && parseInt(ticket.s_id) > 0) onUpdateRemark(ticket, e.target.value);
              else e.target.value = ticket.remark;
            }}
            defaultValue={ticket.remark}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">💬 Remark</option>
            <option value="Checking Query">Checking Query</option>
            <option value="On Leave">On Leave</option>
          </select>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN TICKETS COMPONENT
───────────────────────────────────────────── */
export default function Tickets(props) {
  const history = useHistory();
  const [excellData, setExcelData] = useState("");
  const { sid, uid } = useParams();
  const [search, setSearch] = useState("");
  const [name, setName] = useState("");
  const nameRef = useRef("");
  const [filter, setFilter] = useState("status");
  const [filterSelect, setFilterSelect] = useState("Open");
  const [globalSearch, setGlobalSearch] = useState("");
  const [data, setData] = useState([]);
  const [dataCretaed, setDataCreated] = useState([]);
  const [odataCreated, setODataCreated] = useState([]);
  const [odata, setOData] = useState([]);
  const [filterloader, setfilterLoader] = useState(true);
  const [apiloader, setapiLoader] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [schoolList, setSchoolList] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState("");
  const [replyPopup, setReplyPopup] = useState(null);
  const [highlightedTicketId, setHighlightedTicketId] = useState(null);
  const [assignedFilter, setAssignedFilter] = useState("");
  const [createdFilter, setCreatedFilter] = useState("");
  const handleOpenReply = useCallback((ticket) => { setReplyPopup(ticket); }, []);
  const handleCloseReply = useCallback(() => { setReplyPopup(null); }, []);

  useEffect(() => {
    document.getElementsByTagName("title")[0].innerHTML = "Ticketing System";
    axios
      .post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
        fun_name: "AUserInfo",
        sid: DecryptED(sid),
        u_id: DecryptED(uid),
      })
      .then((res) => {
        if (res.data && res.data !== "INVALID" && res.data.student && res.data.student.length > 0 && res.data.student[0].name) {
          nameRef.current = res.data.student[0].name;
          setName(res.data.student[0].name);
        } else {
          // Fallback: try staffname API
          axios.post("https://marcopolo.prisms.in/api/tscommon/staffname", {
            sid: DecryptED(sid),
            staffid: DecryptED(uid),
          }).then((staffRes) => {
            if (staffRes.data && staffRes.data.length > 0) {
              var s = staffRes.data[0];
              var staffName = (`${s.first_name} ${s.last_name}`).trim();
              nameRef.current = staffName;
              setName(staffName);
            }
          }).catch(() => {});
        }
      })
      .catch(() => {});

    axios({
      url: "https://marcopolo.prisms.in/api/tickets/user/report",
      method: "POST",
      data: { sid: DecryptED(sid), uid: DecryptED(uid) },
      timeout: 30000,
    }).then((res) => {
      var created = res.data || [];
      setDataCreated(created);
      setODataCreated(created);
    }).catch(function(err) {
      console.error('[Tickets] Created tickets fetch failed:', err);
      setDataCreated([]);
      setODataCreated([]);
    });

    props.userType === "superadmin" ? SuperAdminGetTickets() : getTicketCreated(sid);
  }, []);

  // Scroll to last opened ticket when data finishes loading
  useEffect(() => {
    if (!apiloader && !filterloader) {
      var lastId = sessionStorage.getItem("lastOpenedTicketId");
      if (lastId) {
        setHighlightedTicketId(lastId);
        setTimeout(function () {
          var el = document.getElementById("ticket-card-" + lastId);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 300);
        // Remove highlight after 3 seconds
        setTimeout(function () {
          setHighlightedTicketId(null);
        }, 3000);
        sessionStorage.removeItem("lastOpenedTicketId");
      }
    }
  }, [apiloader, filterloader]);

  useEffect(() => {
    setfilterLoader(true);
    let temp = odata;
    let tempCreated = odataCreated;

    // Date range filter - use native Date for performance
    if (startDate && endDate) {
      var startTs = startDate.getTime();
      var endTs = endDate.getTime();
      var dateFilter = function (item) {
        var itemTs = item.date ? Date.parse(item.date.replace(" ", "T")) : 0;
        return itemTs >= startTs && itemTs < endTs;
      };
      temp = temp.filter(dateFilter);
      tempCreated = tempCreated.filter(dateFilter);
    }

    // Status / search filter
    if (filter !== "") {
      temp = temp.filter((d) => {
        if (filter === "status") return d[filter].toString().indexOf(filterSelect) !== -1;
        if (typeof d[filter] === "string") return d[filter].toLowerCase().indexOf(search.toLowerCase()) !== -1;
        return d[filter].toString().indexOf(search) !== -1;
      });
      tempCreated = tempCreated.filter((d) => {
        if (filter === "status") return d[filter] && d[filter].toString().indexOf(filterSelect) !== -1;
        if (typeof d[filter] === "string") return d[filter].toLowerCase().indexOf(search.toLowerCase()) !== -1;
        return d[filter] && d[filter].toString().indexOf(search) !== -1;
      });
    }

    // School filter
    if (selectedSchool && selectedSchool !== "All Schools") {
      temp = temp.filter((d) => String(d.subschool_id) === String(selectedSchool) || String(d.school) === String(selectedSchool));
      tempCreated = tempCreated.filter((d) => String(d.subschool_id) === String(selectedSchool) || String(d.school) === String(selectedSchool));
    }

    // Sort: Open first, then by latest activity (updated_on or date) descending
    // Use native Date parsing (faster than moment) and cache timestamps
    function sortTickets(arr) {
      var withTs = arr.map(function (t) {
        var d = t.updated_on || t.date || "";
        var ts = d ? Date.parse(d.replace(" ", "T")) || 0 : 0;
        return { t: t, ts: ts, open: t.status === "Open" ? 0 : 1 };
      });
      withTs.sort(function (a, b) {
        if (a.open !== b.open) return a.open - b.open;
        return b.ts - a.ts;
      });
      return withTs.map(function (x) { return x.t; });
    }

    setTimeout(() => {
      setData(sortTickets(temp));
      setDataCreated(sortTickets(tempCreated));
      setfilterLoader(false);
    }, 0);
  }, [search, odata, odataCreated, filterSelect, selectedSchool, filter, startDate, endDate]);

  function getTicketCreated(id) {
    const withuid = props.userType === "user" ? { staff_id: uid } : { all: 1 };
    axios
      .post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
        fun_name: "GetTicketCreate",
        sid: id,
        ...withuid,
      })
      .then((res) => {
        var tickets = res.data && res.data.Tickets ? res.data.Tickets : [];
        setOData(tickets);
        setapiLoader(false);
        // Fallback: extract creator name from ticket data if name not set
        if (!nameRef.current && tickets.length > 0) {
          var creatorName = tickets[0].name || tickets[0].creator_name || tickets[0].staff || "";
          if (creatorName) {
            nameRef.current = creatorName;
            setName(creatorName);
          }
        }
      }).catch(function(err) {
        console.error('[Tickets] Assigned tickets fetch failed:', err);
        setOData([]);
        setapiLoader(false);
      });
  }

  function SuperAdminGetTickets() {
    axios
      .post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
        fun_name: "Appointment_Superadmin",
        sid,
      })
      .then(async (res) => {
        if (!res.data || (res.data && res.data.slist && res.data.slist.length === 0) || typeof res.data !== "object") {
          getTicketCreated(sid);
        } else {
          const response = await axios.all(
            res.data.slist.map((s) =>
              axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
                fun_name: "GetTicketCreate",
                sid: s.sid,
              })
            )
          );
          const ticketData = response.map((s) =>
            s.data && s.data.Tickets && typeof s.data.Tickets === "object" ? s.data.Tickets : []
          );
          setOData(
            ticketData.flat().sort((a, b) => {
              const ta = moment(a.updated_on, "YYYY-MM-DD HH:mm:ss").valueOf();
              const tb = moment(b.updated_on, "YYYY-MM-DD HH:mm:ss").valueOf();
              return tb - ta;
            })
          );
          setapiLoader(false);
        }
      }).catch(function(err) {
        console.error('[Tickets] SuperAdmin fetch failed:', err);
        setOData([]);
        setapiLoader(false);
      });
  }

  function updateRemark(data, remark) {
    var d = data;
    delete d.duedate1;
    const d1 = { ...d, remark };
    const fm = new FormData();
    Object.keys(d1).forEach((i) => {
      if (i !== "duedate") fm.append(i === "school" ? "schoolname" : i, d1[i]);
    });
    fm.append("fun_name", "Update_Ticket_Create");
    fm.append("sid", sid);
    setapiLoader(true);
    setfilterLoader(true);
    const nam = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    const dueDateValid =
      d.remark !== "" &&
      new Date(data.duedate).getTime() >= new Date(moment().add(1, "days").format("YYYY-MM-DD HH:mm:ss")).getTime();

    if (dueDateValid) {
      fm.append("duedate", data.duedate);
      axios.all([
        axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
          fun_name: "Update_Ticket_History",
          sid, ticket_id: data.id,
          date: moment().format("YYYY-MM-DD HH:mm:ss"),
          committee: data.committee, remark_reply: remark, reply: 0,
          staff: nam, status: data.status,
          history: `${nam} remarked on ticket`,
          history_details: `${nam} remarked ticket as ${remark}`,
          uid, is_sadmin: props.userType === "superadmin" ? 1 : 0,
        }),
        axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", fm),
      ]).then(() => getTicketCreated(sid));
    } else {
      fm.append("duedate", moment().add(4, "days").format("YYYY-MM-DD HH:mm:ss"));
      axios.all([
        axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
          fun_name: "TicketHistoryInsert",
          sid, ticket_id: data.id,
          date: moment().format("YYYY-MM-DD HH:mm:ss"),
          committee: data.committee, remark_reply: remark, reply: 0,
          staff: nam, status: data.status,
          history: `${nam} remarked on ticket`,
          history_details: `${nam} remarked ticket as ${remark}`,
          uid, is_sadmin: props.userType === "superadmin" ? 1 : 0,
        }),
        axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", fm),
      ]).then(() => getTicketCreated(sid));
    }
  }

  function Push_TO_TicketReply(id, s_id) {
    sessionStorage.setItem("lastOpenedTicketId", id);
    if (props.userType === "superadmin") {
      parseInt(s_id) > 0
        ? history.push(`/tickets/${props.userType}/details/${sid}/${s_id}/${uid}/${id}`, {
          id, username: name.charAt(0).toUpperCase() + name.slice(1).toLowerCase(),
          userType: props.userType, title: "Ticket Details",
        })
        : Swal.fire("Error", "Sorry, There is an issue!", "error");
    } else {
      history.push(`/tickets/${props.userType}/details/${sid}/${uid}/${id}`, {
        id, username: name.charAt(0).toUpperCase() + name.slice(1).toLowerCase(),
        userType: props.userType, title: "Ticket Details",
      });
    }
  }

  function mapTicketToExcel(item) {
    return {
      "Sr No": item.id,
      "Created On": moment(item.created_on).format("DD-MM-YYYY HH:mm:ss"),
      "Ticket Status": item.status,
      "Committe / Department": item.committee,
      "Name of Ticket Creator": item.name,
      "Assigned to": item.staff,
      Title: item.title,
      "Ticket No": item.id,
      Description: item.description,
      Remark: item.remark,
      "School Name": item.school,
      "Updated on": moment(item.updated_on).format("DD-MM-YYYY HH:mm:ss"),
      "Due date": moment(item.duedate).format("DD-MM-YYYY"),
      Priority: item.priority,
    };
  }

  const downloadExcel = () => {
    const wb = XLSX.utils.book_new();

    const assignedData = assignedList.map(mapTicketToExcel);
    const wsAssigned = XLSX.utils.json_to_sheet(assignedData.length > 0 ? assignedData : [{}]);
    XLSX.utils.book_append_sheet(wb, wsAssigned, "Assigned Tickets");

    const createdData = createdList.map(mapTicketToExcel);
    const wsCreated = XLSX.utils.json_to_sheet(createdData.length > 0 ? createdData : [{}]);
    XLSX.utils.book_append_sheet(wb, wsCreated, "Created Tickets");

    XLSX.writeFile(wb, "ticket_report.xlsx");
  };

  useEffect(() => {
    if (data && data.length > 0) {
      setExcelData(data.map((item) => ({
        "Sr No": item.id,
        "Created On": moment(item.created_on).format("DD-MM-YYYY HH:mm:ss"),
        "Ticket Status": item.status,
        "Committe / Department": item.committee,
        "Name of Ticket Creator": item.name,
        "Assigned to": item.staff,
        Title: item.title,
        "Ticket No": item.id,
        Description: item.description,
        Remark: item.remark,
        "School Name": item.school,
        "Updated on": moment(item.updated_on).format("DD-MM-YYYY HH:mm:ss"),
        "Due date": moment(item.duedate).format("DD-MM-YYYY"),
        Priority: item.priority,
      })));
    }
  }, [data]);

  useEffect(() => {
    var allTickets = [].concat(data || []).concat(dataCretaed || []);
    if (allTickets.length > 0) {
      var schools = allTickets
        .filter(function (item) { return item.subschool && item.subschool_id; })
        .map(function (item) { return { subschool: item.subschool, subschool_id: item.subschool_id }; });
      var unique = Array.from(new Map(schools.map(function (s) { return [s.subschool_id, s]; })).values());
      setSchoolList(unique);
    }
  }, [data, dataCretaed]);

  // ── Global search helper ──────────────────────────────
  const matchesGlobalSearch = (d) => {
    if (!globalSearch || globalSearch.trim() === "") return true;
    const q = globalSearch.toLowerCase();
    return (
      (d.title && d.title.toLowerCase().includes(q)) ||
      (d.description && d.description.toLowerCase().includes(q)) ||
      (d.id && String(d.id).includes(q)) ||
      (d.staff && d.staff.toLowerCase().includes(q)) ||
      (d.name && d.name.toLowerCase().includes(q)) ||
      (d.committee && d.committee.toLowerCase().includes(q)) ||
      (d.status && d.status.toLowerCase().includes(q)) ||
      (d.remark && d.remark.toLowerCase().includes(q)) ||
      (d.school && d.school.toLowerCase().includes(q)) ||
      (d.subschool && d.subschool.toLowerCase().includes(q)) ||
      (d.priority && d.priority.toLowerCase().includes(q))
    );
  };

  // ── Derived lists (memoized for performance) ──────────────────────────────
  const decUid = useMemo(() => DecryptED(uid), [uid]);

  const assignedList = useMemo(() => {
    return data.filter(function (d) {
      return (
        String(d.staff_id) === String(decUid) ||
        String(d.u_id) === String(decUid) ||
        String(d.assigned_to) === String(decUid) ||
        String(d.assignee_id) === String(decUid) ||
        (name && d.staff && d.staff.toLowerCase() === name.toLowerCase())
      );
    }).filter(matchesGlobalSearch);
  }, [data, decUid, name, globalSearch]);

  const createdList = useMemo(() => {
    return data.filter(function (d) {
      return (
        String(d.uid) === String(decUid) ||
        String(d.creator_id) === String(decUid) ||
        String(d.created_by) === String(decUid) ||
        String(d.user_id) === String(decUid) ||
        (name && d.name && d.name.toLowerCase() === name.toLowerCase())
      );
    }).filter(matchesGlobalSearch);
  }, [data, decUid, name, globalSearch]);

  // Stats
  const openCount = data.filter((d) => d.status === "Open").length;
  const closedCount = data.filter((d) => d.status === "Closed").length;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        :root {
          --navy:        #0b2545;
          --navy-mid:    #134074;
          --navy-light:  #1b5499;
          --accent:      #2d9cdb;
          --accent-soft: #e8f5fd;
          --bg:          #f4f7fb;
          --surface:     #ffffff;
          --border:      #e2eaf4;
          --text-1:      #0b2545;
          --text-2:      #4a6080;
          --text-3:      #94a3b8;
          --radius:      12px;
          --shadow-sm:   0 1px 3px rgba(11,37,69,0.08), 0 4px 12px rgba(11,37,69,0.05);
          --shadow-md:   0 4px 20px rgba(11,37,69,0.10);
          --shadow-lg:   0 8px 40px rgba(11,37,69,0.14);
        }

        .tk-wrap * { box-sizing: border-box; margin: 0; padding: 0; }
        .tk-wrap {
          min-height: 100vh;
          background: var(--bg);
          font-family: 'Plus Jakarta Sans', sans-serif;
          color: var(--text-1);
        }

        /* ── Topbar ── */
        .tk-topbar {
          background: linear-gradient(135deg, var(--navy) 0%, var(--navy-mid) 60%, var(--navy-light) 100%);
          height: 60px;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 32px;
          box-shadow: 0 2px 12px rgba(11,37,69,0.25);
          position: sticky; top: 0; z-index: 50;
        }
        .tk-topbar-left { display: flex; align-items: center; gap: 12px; }
        .tk-topbar-icon {
          width: 34px; height: 34px; background: rgba(255,255,255,0.15);
          border-radius: 8px; display: flex; align-items: center; justify-content: center;
          font-size: 16px; backdrop-filter: blur(4px);
        }
        .tk-topbar-title { font-size: 17px; font-weight: 800; color: #fff; letter-spacing: -0.3px; }
        .tk-topbar-nav { display: flex; gap: 6px; }
        .tk-nav-btn {
          padding: 7px 16px; border-radius: 8px; font-size: 13px; font-weight: 600;
          color: rgba(255,255,255,0.85); cursor: pointer; transition: all 0.15s;
          border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.08);
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        .tk-nav-btn:hover { background: rgba(255,255,255,0.18); color: #fff; }
        .tk-nav-btn-primary { background: var(--accent) !important; border-color: var(--accent) !important; color: #fff !important; }

        /* ── Filter Bar ── */
        .tk-topfilter {
          background: var(--surface); border-bottom: 1px solid var(--border);
          padding: 0 32px; display: flex; align-items: center; gap: 8px;
          flex-wrap: nowrap; overflow-x: auto; height: 52px;
          box-shadow: 0 1px 4px rgba(11,37,69,0.06);
          position: sticky; top: 60px; z-index: 40;
        }
        .tk-topfilter::-webkit-scrollbar { height: 3px; }
        .tk-topfilter::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }
        .tk-tf-label { font-size: 11px; font-weight: 800; color: var(--text-3); text-transform: uppercase; letter-spacing: 0.8px; flex-shrink: 0; }
        .tk-tf-divider { width: 1px; height: 22px; background: var(--border); flex-shrink: 0; }
        .tk-tf-select {
          height: 32px; flex-shrink: 0; padding: 0 26px 0 10px;
          border: 1.5px solid var(--border); border-radius: 7px;
          font-size: 12.5px; font-weight: 600; font-family: 'Plus Jakarta Sans', sans-serif;
          color: var(--text-1);
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%234a6080' d='M6 7L1 2h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 8px center;
          background-color: var(--bg); appearance: none; cursor: pointer; outline: none;
          min-width: 130px; transition: border-color 0.15s;
        }
        .tk-tf-select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(45,156,219,0.12); background: #fff; }
        .tk-tf-input {
          height: 32px; flex-shrink: 0; padding: 0 10px;
          border: 1.5px solid var(--border); border-radius: 7px;
          font-size: 12.5px; font-weight: 500; font-family: 'Plus Jakarta Sans', sans-serif;
          color: var(--text-1); background: var(--bg); outline: none; min-width: 140px;
          transition: border-color 0.15s;
        }
        .tk-tf-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(45,156,219,0.12); background: #fff; }
        .tk-tf-badge {
          height: 32px; flex-shrink: 0; padding: 0 12px; border-radius: 7px;
          background: var(--accent-soft); border: 1.5px solid var(--accent);
          font-size: 12px; font-weight: 700; color: var(--navy-light);
          display: inline-flex; align-items: center; gap: 4px; white-space: nowrap;
        }
        .tk-tf-dl {
          height: 32px; flex-shrink: 0; padding: 0 14px;
          background: linear-gradient(135deg, var(--navy) 0%, var(--navy-light) 100%);
          color: #fff; border: none; border-radius: 7px;
          font-size: 12px; font-weight: 700; font-family: 'Plus Jakarta Sans', sans-serif;
          cursor: pointer; display: inline-flex; align-items: center; gap: 5px;
          box-shadow: 0 2px 8px rgba(11,37,69,0.2); transition: all 0.15s;
          white-space: nowrap; margin-left: auto;
        }
        .tk-tf-dl:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(11,37,69,0.28); }

        /* ── Breadcrumb ── */
        .tk-breadcrumb {
          background: var(--bg); padding: 8px 32px;
          display: flex; align-items: center; gap: 8px; font-size: 12px;
        }
        .tk-bc-link { color: var(--text-2); cursor: pointer; font-weight: 500; transition: color 0.15s; }
        .tk-bc-link:hover { color: var(--accent); }
        .tk-bc-sep { color: var(--text-3); }
        .tk-bc-active { color: var(--navy); font-weight: 700; }

        .tk-body { padding: 20px 32px 60px; }

        /* ── Stats ── */
        .tk-stats {
          display: grid; grid-template-columns: repeat(5, 1fr);
          gap: 14px; margin-bottom: 22px;
          animation: tkFadeUp 0.35s ease both;
        }
        @keyframes tkFadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        .tk-stat {
          background: var(--surface); border-radius: var(--radius);
          border: 1px solid var(--border); padding: 16px 18px;
          display: flex; align-items: center; gap: 12px;
          box-shadow: var(--shadow-sm); transition: all 0.18s;
        }
        .tk-stat:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
        .tk-stat-icon {
          width: 42px; height: 42px; border-radius: 11px;
          display: flex; align-items: center; justify-content: center;
          font-size: 19px; flex-shrink: 0;
        }
        .tk-stat-label { font-size: 11px; font-weight: 700; color: var(--text-3); text-transform: uppercase; letter-spacing: 0.6px; }
        .tk-stat-value { font-size: 24px; font-weight: 800; color: var(--navy); letter-spacing: -0.5px; margin-top: 1px; }

        /* ── Cards ── */
        .tk-cards { display: flex; flex-direction: column; gap: 12px; }
        .tk-card {
          background: var(--surface); border-radius: var(--radius);
          border: 1px solid var(--border); box-shadow: var(--shadow-sm);
          overflow: hidden; transition: all 0.18s; animation: tkFadeUp 0.35s ease both;
        }
        .tk-card:hover { box-shadow: var(--shadow-md); transform: translateY(-2px); border-color: #c5d8ef; }
        .tk-card-top {
          padding: 14px 20px 12px;
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 12px; cursor: pointer;
        }
        .tk-card-left { flex: 1; min-width: 0; }
        .tk-card-title-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; flex-wrap: wrap; }
        .tk-ticket-id {
          font-size: 11.5px; font-weight: 800; color: var(--accent);
          background: var(--accent-soft); padding: 3px 9px; border-radius: 20px;
          flex-shrink: 0;
        }
        .tk-card-title { font-size: 15px; font-weight: 700; color: var(--navy); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .tk-card-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 14px; font-size: 12.5px; color: var(--text-2); font-weight: 500; }
        .tk-meta-item { display: flex; align-items: center; gap: 5px; }
        .tk-meta-icon { font-size: 13px; }
        .tk-card-badges { display: flex; align-items: center; gap: 7px; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end; }
        .tk-badge { font-size: 12px; font-weight: 700; padding: 4px 12px; border-radius: 20px; border: 1.5px solid; white-space: nowrap; }
        .tk-card-bottom {
          padding: 10px 20px 12px; background: #fafcff;
          border-top: 1px solid var(--border);
          display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;
        }
        .tk-duedate { font-size: 12px; font-weight: 600; color: var(--text-2); display: flex; align-items: center; gap: 5px; }
        .tk-duedate-val { background: #d3ffeb; color: #16a34a; padding: 3px 10px; border-radius: 20px; font-weight: 700; font-size: 12px; }
        .tk-remark-select {
          height: 34px; padding: 0 28px 0 10px;
          border: 1.5px solid var(--border); border-radius: 8px;
          font-size: 12.5px; font-weight: 600; font-family: 'Plus Jakarta Sans', sans-serif;
          color: var(--text-1); background: var(--surface);
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%234a6080' d='M5 7L1 2h8z'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 8px center;
          background-color: var(--surface); appearance: none; cursor: pointer; outline: none;
          transition: border-color 0.15s;
        }
        .tk-remark-select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(45,156,219,0.12); }

        /* ── Empty ── */
        .tk-empty {
          text-align: center; padding: 80px 20px;
          background: var(--surface); border-radius: var(--radius);
          border: 1px solid var(--border); box-shadow: var(--shadow-sm);
        }
        .tk-empty-icon { font-size: 52px; margin-bottom: 16px; }
        .tk-empty-title { font-size: 18px; font-weight: 800; color: var(--navy); margin-bottom: 6px; }
        .tk-empty-sub { font-size: 13px; color: var(--text-2); }

        .tk-loader { display: flex; align-items: center; justify-content: center; min-height: 50vh; }

        @media (max-width: 1024px) { .tk-stats { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 768px) {
          .tk-body { padding: 16px 12px 60px; }
          .tk-topbar { padding: 0 12px; height: 52px; }
          .tk-topbar-title { font-size: 14px; }
          .tk-topbar-icon { width: 30px; height: 30px; font-size: 14px; }
          .tk-topfilter { padding: 8px 12px; height: auto; min-height: 44px; flex-wrap: wrap; gap: 6px; }
          .tk-breadcrumb { padding: 6px 12px; }
          .tk-stats { grid-template-columns: repeat(2, 1fr); gap: 8px; }
          .tk-card-top { flex-direction: column; padding: 12px 14px 10px; }
          .tk-card-badges { justify-content: flex-start; }
          .tk-card-bottom { padding: 8px 14px 10px; }
          .tk-nav-btn { padding: 6px 12px; font-size: 12px; }
        }
        @media (max-width: 480px) {
          .tk-wrap { overflow-x: hidden; }
          .tk-stats { grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 14px; }
          .tk-stat { padding: 12px; gap: 8px; }
          .tk-stat-icon { width: 34px; height: 34px; border-radius: 8px; font-size: 16px; }
          .tk-stat-value { font-size: 20px; }
          .tk-stat-label { font-size: 10px; }
          .tk-topbar-responsive {
            flex-direction: column !important;
            height: auto !important;
            padding: 8px 10px !important;
            gap: 6px !important;
          }
          .tk-topbar-responsive .tk-topbar-left { width: 100%; }
          .tk-topbar-responsive .tk-topbar-nav { width: 100%; justify-content: flex-end !important; }
          .tk-topbar-responsive .tk-topbar-left { flex-wrap: wrap; }
          .tk-topbar-title { font-size: 12px; }
          .tk-welcome-text { font-size: 12px !important; white-space: normal !important; line-height: 1.3; }
          .tk-nav-btn { padding: 5px 8px; font-size: 10px; }
          .tk-topfilter { padding: 6px 10px; gap: 5px; }
          .tk-tf-select { min-width: 100px; height: 30px; font-size: 11.5px; padding: 0 22px 0 8px; }
          .tk-tf-input { min-width: 100px; height: 30px; font-size: 11.5px; padding: 0 8px; }
          .tk-tf-label { font-size: 10px; }
          .tk-tf-badge { height: 28px; font-size: 11px; padding: 0 10px; }
          .tk-tf-dl { height: 30px; font-size: 11px; padding: 0 10px; }
          .tk-body { padding: 10px 10px 60px; }
          .tk-breadcrumb { padding: 6px 10px; font-size: 11px; }
          .tk-card { border-radius: 8px; }
          .tk-card-top { padding: 10px 12px 8px; gap: 8px; }
          .tk-card-title { font-size: 13.5px; }
          .tk-card-meta { font-size: 11.5px; gap: 8px; }
          .tk-ticket-id { font-size: 10.5px; padding: 2px 7px; }
          .tk-badge { font-size: 11px; padding: 3px 10px; }
          .tk-card-bottom { padding: 8px 12px; gap: 6px; }
          .tk-duedate { font-size: 11px; }
          .tk-duedate-val { font-size: 11px; padding: 2px 8px; }
          .tk-remark-select { height: 30px; font-size: 11.5px; }
          .tk-empty { padding: 40px 16px; }
          .tk-empty-icon { font-size: 40px; }
          .tk-empty-title { font-size: 16px; }
          .tk-empty-sub { font-size: 12px; }
        }
        @media (max-width: 360px) {
          .tk-stats { grid-template-columns: 1fr; }
          .tk-topbar-title { font-size: 12px; }
          .tk-nav-btn { padding: 4px 8px; font-size: 10px; }
          .tk-tf-select, .tk-tf-input { min-width: 80px; }
        }

        /* ── Split View ── */
        .tk-split-view {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
          align-items: start;
        }
        .tk-split-col {
          background: var(--surface);
          border-radius: var(--radius);
          border: 1px solid var(--border);
          box-shadow: var(--shadow-sm);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .tk-split-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 13px 18px;
        }
        .tk-split-assigned {
          background: linear-gradient(135deg, #0b2545 0%, #1b5499 100%);
        }
        .tk-split-created {
          background: linear-gradient(135deg, #78350f 0%, #b45309 100%);
        }
        .tk-split-header-left {
          display: flex; align-items: center; gap: 8px;
        }
        .tk-split-icon { font-size: 16px; }
        .tk-split-title {
          font-size: 13.5px; font-weight: 800; color: #fff; letter-spacing: -0.2px;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        .tk-split-count {
          background: rgba(255,255,255,0.2); color: #fff;
          padding: 3px 11px; border-radius: 20px;
          font-size: 12px; font-weight: 800;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        .tk-split-body {
          padding: 12px;
          display: flex; flex-direction: column; gap: 10px;
        }

        .tk-split-body .tk-card-title { font-size: 13.5px; }
        .tk-split-body .tk-card-meta { font-size: 11.5px; gap: 10px; }

        @media (max-width: 900px) {
          .tk-split-view { grid-template-columns: 1fr; }
        }
        @media (max-width: 480px) {
          .tk-split-view { gap: 10px; }
          .tk-split-header { padding: 10px 14px; }
          .tk-split-title { font-size: 12.5px; }
          .tk-split-count { font-size: 11px; padding: 2px 9px; }
          .tk-split-body { padding: 8px; gap: 8px; }
          .tk-split-body .tk-card-title { font-size: 12.5px; }
          .tk-split-body .tk-card-meta { font-size: 10.5px; }
        }
      `}</style>

      <div className="tk-wrap">

        {/* Reply Popup — rendered outside tk-wrap's CSS scope */}

        {/* Topbar */}
        <div className="tk-topbar tk-topbar-responsive">
          <div className="tk-topbar-left">
            <div className="tk-topbar-icon">🎫</div>
            <span className="tk-topbar-title">Ticket Management</span>
            <span style={{ color: "rgba(255,255,255,0.3)", margin: "0 4px" }}>|</span>
            <span className="tk-welcome-text" style={{ color: "#7dd3fc", fontSize: "13px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
              Welcome, {name || "User"}
            </span>
          </div>
          <div className="tk-topbar-nav" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {props.options && props.options.map((opt, i) => (
              <button key={i}
                className={`tk-nav-btn ${i === props.options.length - 1 ? "tk-nav-btn-primary" : ""}`}
                onClick={() => history.push(opt.url)}
              >
                {i === props.options.length - 1 ? "+ " : ""}{opt.title}
              </button>
            ))}
          </div>
        </div>

        {/* Filter Bar */}
        <div className="tk-topfilter">
          <span className="tk-tf-label">Filters</span>
          <div className="tk-tf-divider" />
          <select className="tk-tf-select" onChange={(e) => { setSearch(""); setFilter(e.target.value); }} defaultValue={filter}>
            <option value="status">Status</option>
            <option value="committee">Department</option>
            <option value="staff">Staff</option>
            <option value="title">Title</option>
            <option value="duedate">Due Date</option>
            <option value="id">Ticket No</option>
            <option value="remark">Remark</option>
            <option value="name">Name</option>
          </select>
          {filter === "status" ? (
            <select className="tk-tf-select" style={{ minWidth: "110px" }} value={filterSelect} onChange={(e) => setFilterSelect(e.target.value)}>
              <option value="">All</option>
              <option value="Open">Open</option>
              <option value="Closed">Closed</option>
            </select>
          ) : (
            <input type={filter === "duedate" ? "date" : "text"} className="tk-tf-input"
              placeholder="Search..." defaultValue={search} onChange={(e) => setSearch(e.target.value)}
              style={{ width: "180px", minWidth: "140px", maxWidth: "200px" }} />
          )}
          {filter === "status" && (
            <div className="tk-tf-badge">🎫 {filterSelect || "All"} · {assignedList.length + createdList.length}</div>
          )}
          <div className="tk-tf-divider" />
          <select className="tk-tf-select" onChange={(e) => setSelectedSchool(e.target.value)}>
            <option value="All Schools">All Schools</option>
            {schoolList.map((s, i) => <option key={i} value={s.subschool_id}>{s.subschool}</option>)}
          </select>
          <div className="tk-tf-divider" />
          <input type="date"
            value={startDate ? startDate.toISOString().split("T")[0] : ""}
            onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
            style={{ height: "32px", padding: "0 6px", border: "1.5px solid var(--border)", borderRadius: "7px", fontSize: "12px", fontFamily: "inherit", background: "var(--bg)", outline: "none", width: "130px", maxWidth: "130px", flexShrink: 0 }}
          />
          <span style={{ fontSize: "11px", color: "var(--text-3)", fontWeight: 700, flexShrink: 0 }}>→</span>
          <input type="date"
            value={endDate ? endDate.toISOString().split("T")[0] : ""}
            onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
            style={{ height: "32px", padding: "0 6px", border: "1.5px solid var(--border)", borderRadius: "7px", fontSize: "12px", fontFamily: "inherit", background: "var(--bg)", outline: "none", width: "130px", maxWidth: "130px", flexShrink: 0 }}
          />
          <div className="tk-tf-divider" />
          <input type="text" className="tk-tf-input"
            placeholder="🔍 Search tickets..."
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            style={{ width: "200px", minWidth: "150px", maxWidth: "220px" }}
          />
          <button className="tk-tf-dl" onClick={downloadExcel}>⬇ Excel</button>
        </div>

        {/* Breadcrumb */}
        <div className="tk-breadcrumb">
          <span className="tk-bc-link" onClick={() => history.go(-1)}>Home</span>
          <span className="tk-bc-sep">›</span>
          <span className="tk-bc-active">{props.title}</span>
        </div>

        <div className="tk-body">

          {/* Stats are now shown above each section */}

          {/* ── SPLIT VIEW ── */}
          {filterloader || apiloader ? (
            <div className="tk-loader">
              <Loader type="Puff" color="#134074" height={70} width={70} />
            </div>
          ) : (
            <div className="tk-split-view">

              {/* LEFT: Assigned to Me */}
              <div className="tk-split-col">
                <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                  <div onClick={function(){ setAssignedFilter(""); }} style={{ flex: 1, background: assignedFilter === "" ? "#134074" : "#e8f5fd", borderRadius: "8px", padding: "8px 12px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: assignedFilter === "" ? "#fff" : "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total</div>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: assignedFilter === "" ? "#fff" : "#134074" }}>{assignedList.length}</div>
                  </div>
                  <div onClick={function(){ setAssignedFilter(assignedFilter === "Open" ? "" : "Open"); }} style={{ flex: 1, background: assignedFilter === "Open" ? "#dc2626" : "#fef2f2", borderRadius: "8px", padding: "8px 12px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: assignedFilter === "Open" ? "#fff" : "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Open</div>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: assignedFilter === "Open" ? "#fff" : "#dc2626" }}>{assignedList.filter(function(t){ return t.status === "Open"; }).length}</div>
                  </div>
                  <div onClick={function(){ setAssignedFilter(assignedFilter === "Closed" ? "" : "Closed"); }} style={{ flex: 1, background: assignedFilter === "Closed" ? "#16a34a" : "#f0fdf4", borderRadius: "8px", padding: "8px 12px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: assignedFilter === "Closed" ? "#fff" : "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Closed</div>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: assignedFilter === "Closed" ? "#fff" : "#16a34a" }}>{assignedList.filter(function(t){ return t.status === "Closed"; }).length}</div>
                  </div>
                </div>
                <div className="tk-split-header tk-split-assigned">
                  <div className="tk-split-header-left">
                    <span className="tk-split-icon">📥</span>
                    <span className="tk-split-title">Assigned to Me</span>
                  </div>
                  <span className="tk-split-count">{(assignedFilter ? assignedList.filter(function(t){ return t.status === assignedFilter; }) : assignedList).length}</span>
                </div>
                <div className="tk-split-body">
                  {(assignedFilter ? assignedList.filter(function(t){ return t.status === assignedFilter; }) : assignedList).length === 0 ? (
                    <div className="tk-empty" style={{ padding: "48px 20px" }}>
                      <div className="tk-empty-icon" style={{ fontSize: "36px" }}>📭</div>
                      <div className="tk-empty-title" style={{ fontSize: "15px" }}>No {assignedFilter || ""} Assigned Tickets</div>
                      <div className="tk-empty-sub">No {assignedFilter ? assignedFilter.toLowerCase() : ""} tickets assigned to you</div>
                    </div>
                  ) : (
                    <div className="tk-cards">
                      {(assignedFilter ? assignedList.filter(function(t){ return t.status === assignedFilter; }) : assignedList).map(function (ticket, k) {
                        return <TicketCard ticket={ticket} k={k} key={ticket.id || k} onReply={handleOpenReply} onUpdateRemark={updateRemark} onNavigate={Push_TO_TicketReply} sid={sid} isHighlighted={String(ticket.id) === String(highlightedTicketId)} searchQuery={globalSearch} />;
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT: Created by Me */}
              <div className="tk-split-col">
                <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                  <div onClick={function(){ setCreatedFilter(""); }} style={{ flex: 1, background: createdFilter === "" ? "#134074" : "#e8f5fd", borderRadius: "8px", padding: "8px 12px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: createdFilter === "" ? "#fff" : "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total</div>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: createdFilter === "" ? "#fff" : "#134074" }}>{createdList.length}</div>
                  </div>
                  <div onClick={function(){ setCreatedFilter(createdFilter === "Open" ? "" : "Open"); }} style={{ flex: 1, background: createdFilter === "Open" ? "#dc2626" : "#fef2f2", borderRadius: "8px", padding: "8px 12px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: createdFilter === "Open" ? "#fff" : "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Open</div>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: createdFilter === "Open" ? "#fff" : "#dc2626" }}>{createdList.filter(function(t){ return t.status === "Open"; }).length}</div>
                  </div>
                  <div onClick={function(){ setCreatedFilter(createdFilter === "Closed" ? "" : "Closed"); }} style={{ flex: 1, background: createdFilter === "Closed" ? "#16a34a" : "#f0fdf4", borderRadius: "8px", padding: "8px 12px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: createdFilter === "Closed" ? "#fff" : "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Closed</div>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: createdFilter === "Closed" ? "#fff" : "#16a34a" }}>{createdList.filter(function(t){ return t.status === "Closed"; }).length}</div>
                  </div>
                </div>
                <div className="tk-split-header tk-split-created">
                  <div className="tk-split-header-left">
                    <span className="tk-split-icon">✏️</span>
                    <span className="tk-split-title">Created by Me</span>
                  </div>
                  <span className="tk-split-count">{(createdFilter ? createdList.filter(function(t){ return t.status === createdFilter; }) : createdList).length}</span>
                </div>
                <div className="tk-split-body">
                  {(createdFilter ? createdList.filter(function(t){ return t.status === createdFilter; }) : createdList).length === 0 ? (
                    <div className="tk-empty" style={{ padding: "48px 20px" }}>
                      <div className="tk-empty-icon" style={{ fontSize: "36px" }}>📭</div>
                      <div className="tk-empty-title" style={{ fontSize: "15px" }}>No {createdFilter || ""} Created Tickets</div>
                      <div className="tk-empty-sub">No {createdFilter ? createdFilter.toLowerCase() : ""} tickets created by you</div>
                    </div>
                  ) : (
                    <div className="tk-cards">
                      {(createdFilter ? createdList.filter(function(t){ return t.status === createdFilter; }) : createdList).map(function (ticket, k) {
                        return <TicketCard ticket={ticket} k={k} key={ticket.id || k} onReply={handleOpenReply} onUpdateRemark={updateRemark} onNavigate={Push_TO_TicketReply} sid={sid} isHighlighted={String(ticket.id) === String(highlightedTicketId)} searchQuery={globalSearch} />;
                      })}
                    </div>
                  )}
                </div>
              </div>

            </div>
          )}

        </div>
      </div>

      {/* Reply Popup — outside tk-wrap to avoid CSS interference */}
      {replyPopup &&
        ReactDOM.createPortal(
          <ReplyPopup
            ticket={replyPopup}
            onClose={handleCloseReply}
            onSubmit={() => getTicketCreated(sid)}
            userName={name}
            sid={sid}
            uid={uid}
            userType={props.userType}
          />,
          document.body
        )
      }
    </>
  );
}
